import { ImportEntityTypeModel } from '@utmost.co/enterprise-models';
import { FieldMetadataElementValidator } from 'src/components/fieldMetadata/FieldMetadataElementValidator';
import { PartialFieldMetaDataOverride } from 'src/utils/hooks/data/useFieldMetaData';
import { CommonFieldDefinitionData, ImportColumnData } from './utils/useImporterData';
import { Validation, Checkbox, Select, Input, MultiSelect } from '@beeline.com/react-spreadsheet-import';
import { RsiField, RsiFieldKey } from './Types';
import { filterNotEmpty } from '@beeline.com/bp-design-system';
import {
  doesFieldDefinitionSupportEntityExistsById,
  doesFieldDefinitionSupportReferenceIdValidation
} from './validations/impl/validationUtils';
import { importTypeToEntityURNs } from './utils/Importer.constants';

export const getRsiFieldEnumOptions = (
  importColumn: ImportColumnData,
  fieldDefinition: PartialFieldMetaDataOverride | undefined
) => {
  if (importColumn?.values || importColumn?.values?.length) {
    return importColumn.values.map((value) => ({
      value: value?.value ?? '',
      label: value?.label ?? ''
    }));
  } else if (fieldDefinition?.values && fieldDefinition?.values?.length > 0) {
    return fieldDefinition.values.map((value) => ({
      value: value.value,
      label: value.label
    }));
  } else return [];
};

export const getRsiFieldType = (
  importColumn: ImportColumnData,
  fieldDefinition: PartialFieldMetaDataOverride | undefined
): Checkbox | Select | MultiSelect | Input => {
  if ((importColumn?.values && importColumn.values.length > 0) || fieldDefinition?.type === 'ENUM') {
    if (fieldDefinition?.multivalued) {
      return {
        type: 'multiselect',
        valueSeparator: ',',
        options: getRsiFieldEnumOptions(importColumn, fieldDefinition),
        caseInsensitiveValueMatch: true
      } as MultiSelect;
    }
    return {
      type: 'select',
      multivalued: fieldDefinition?.multivalued ?? false,
      options: getRsiFieldEnumOptions(importColumn, fieldDefinition),
      caseInsensitiveValueMatch: true
    } as Select;
  }

  if (fieldDefinition?.type === 'BOOLEAN') {
    return { type: 'checkbox' } as Checkbox;
  }

  if (fieldDefinition?.type === 'DATE' || fieldDefinition?.type === 'DATETIME') {
    return { type: 'input' } as Input;
  }

  if (['NUMBER', 'INTEGER', 'DECIMAL'].includes(fieldDefinition?.type || '')) {
    return { type: 'input' } as Input;
  }

  return { type: 'input' } as Input;
};

const translateToRsiField = (
  importColumn: ImportColumnData,
  fieldDefinition: PartialFieldMetaDataOverride | undefined,
  fieldsForExistenceDetection: RsiFieldKey[],
  conditionallyRequiredFields: RsiFieldKey[],
  requireExistenceDetectionFieldsAreUnique: boolean
): RsiField => {
  const fieldType = getRsiFieldType(importColumn, fieldDefinition);

  const metadataValidations: Validation[] = [];

  // Add unique validation rule if field is required for existence detection
  if (requireExistenceDetectionFieldsAreUnique && fieldsForExistenceDetection.includes(importColumn.urn ?? '')) {
    metadataValidations.push({
      rule: 'unique',
      allowEmpty: true,
      errorMessage: 'Field is not unique, another row also contains this value',
      level: 'error'
    });
  }

  // Requiredness is also performed during validateUsingMetadata
  if (conditionallyRequiredFields.includes(importColumn.urn ?? '') || fieldDefinition?.required === true) {
    metadataValidations.push({
      rule: 'required',
      errorMessage: 'Field is required for this data object',
      level: 'error'
    });
  }

  // // Regex validations are performed during: validateRecordClientSide
  // if (fieldDefinition?.regex) {
  //   metadataValidations.push({
  //     rule: 'regex',
  //     value: fieldDefinition.regex,
  //     errorMessage: fieldDefinition.message,
  //     level: 'error'
  //   });
  // }

  return {
    label: importColumn.label ?? '',
    key: importColumn.urn ?? '',
    // description: '',
    alternateMatches: [importColumn.label ?? ''], // TODO: add search based on key for potential matches
    validations: metadataValidations,
    fieldType: fieldType
    // example: string
  };
};

export const getRsiFieldsForBackendValidation = (
  columns: ImportColumnData[] | undefined,
  urnToFieldMetadataDefinitionMap: Map<string, CommonFieldDefinitionData>
): {
  fieldsForExistenceDetection: RsiFieldKey[];
  fieldsForReferenceIdValidation: RsiFieldKey[];
} => {
  if (!columns || columns.length === 0) {
    return { fieldsForExistenceDetection: [], fieldsForReferenceIdValidation: [] };
  }

  const fieldsForExistenceDetection =
    filterNotEmpty(
      columns
        ?.filter((el) => doesFieldDefinitionSupportEntityExistsById(urnToFieldMetadataDefinitionMap.get(el.urn ?? '')))
        .map((el) => el.urn)
    ) ?? ([] as RsiFieldKey[]);

  const fieldsForReferenceIdValidation =
    filterNotEmpty(
      columns
        ?.filter((el) =>
          doesFieldDefinitionSupportReferenceIdValidation(urnToFieldMetadataDefinitionMap.get(el.urn ?? ''))
        )
        .map((el) => el.urn)
    ) ?? ([] as RsiFieldKey[]);

  return {
    fieldsForExistenceDetection,
    fieldsForReferenceIdValidation
  };
};

export const getRsiFields = (
  entityType: ImportEntityTypeModel | undefined,
  columns: ImportColumnData[] | undefined,
  urnToFieldMetadataDefinitionMap: Map<string, CommonFieldDefinitionData>,
  fieldMetadataValidator: FieldMetadataElementValidator,
  requireExistenceDetectionFieldsAreUnique: boolean | undefined
): {
  fieldDefinitions: RsiField[];
  fieldsForExistenceDetection: RsiFieldKey[];
  fieldsForReferenceIdValidation: RsiFieldKey[];
} => {
  if (!columns || columns.length === 0 || !entityType) {
    return { fieldDefinitions: [], fieldsForExistenceDetection: [], fieldsForReferenceIdValidation: [] };
  }

  const { fieldsForExistenceDetection, fieldsForReferenceIdValidation } = getRsiFieldsForBackendValidation(
    columns,
    urnToFieldMetadataDefinitionMap
  );

  // Get all fields that are conditionally required for the import type
  const conditionallyRequiredFields =
    importTypeToEntityURNs
      .get(entityType)
      ?.flatMap((entityURN) => fieldMetadataValidator?.getRequiredFieldsFor(entityURN, {}, new Map())) ?? [];

  const fieldDefinitions =
    columns?.map((importColumn) => {
      const fieldDefinition = fieldMetadataValidator.getFieldMetadataFor(importColumn.urn ?? '');
      return translateToRsiField(
        importColumn,
        fieldDefinition,
        fieldsForExistenceDetection,
        conditionallyRequiredFields,
        requireExistenceDetectionFieldsAreUnique ?? false
      );
    }) ?? ([] as RsiField[]);

  return {
    fieldDefinitions,
    fieldsForExistenceDetection,
    fieldsForReferenceIdValidation
  };
};
